import "./MentionHoverCard.scss"

import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import ReactDOM from "react-dom"
import { Avatar } from "./common"

// Tùy biến KHCN: rê chuột (hoặc chạm trên thiết bị cảm ứng) vào @tên trong bình luận thì hiện thẻ nhỏ
// có ảnh đại diện, tên và đơn vị. Thẻ đo kích thước thật rồi mới đặt: dưới tên nếu đủ chỗ, không thì lật
// lên trên, và luôn nằm trong màn hình. Danh sách người được tra là tác giả bài và những người đã bình luận.

export interface MentionUser {
  id?: number
  name: string
  avatarURL: string
  organization?: string
  province?: string
}

interface MentionHoverCardProps {
  users: MentionUser[]
  children: React.ReactNode
}

interface Anchor {
  user: MentionUser
  element: HTMLElement
  rect: DOMRect
}

const HIDE_DELAY = 120
const VIEWPORT_MARGIN = 8
const GAP = 6

export const MentionHoverCard = (props: MentionHoverCardProps) => {
  const [anchor, setAnchor] = useState<Anchor | undefined>(undefined)
  const [position, setPosition] = useState<{ top: number; left: number } | undefined>(undefined)
  const cardRef = useRef<HTMLDivElement>(null)
  const hideTimer = useRef<number>()

  const cancelHide = () => window.clearTimeout(hideTimer.current)
  const close = () => {
    cancelHide()
    setAnchor(undefined)
    setPosition(undefined)
  }
  const scheduleHide = () => {
    cancelHide()
    hideTimer.current = window.setTimeout(close, HIDE_DELAY)
  }

  useEffect(() => cancelHide, [])

  // Đặt thẻ sau khi đã đo kích thước thật
  useLayoutEffect(() => {
    if (!anchor || !cardRef.current) {
      return
    }
    const { width, height } = cardRef.current.getBoundingClientRect()
    const rect = anchor.rect
    let top = rect.bottom + GAP
    if (top + height > window.innerHeight - VIEWPORT_MARGIN) {
      top = rect.top - height - GAP
    }
    if (top < VIEWPORT_MARGIN) {
      top = VIEWPORT_MARGIN
    }
    const left = Math.max(VIEWPORT_MARGIN, Math.min(rect.left, window.innerWidth - width - VIEWPORT_MARGIN))
    setPosition({ top, left })
  }, [anchor])

  // Cuộn hoặc đổi kích thước thì đóng để thẻ không lệch khỏi tên
  useEffect(() => {
    if (!anchor) {
      return
    }
    window.addEventListener("scroll", close, true)
    window.addEventListener("resize", close)
    return () => {
      window.removeEventListener("scroll", close, true)
      window.removeEventListener("resize", close)
    }
  }, [anchor])

  const mentionTarget = (e: React.SyntheticEvent): HTMLElement | null => {
    const el = e.target as HTMLElement
    return el && typeof el.closest === "function" ? (el.closest(".mention") as HTMLElement | null) : null
  }

  const open = (target: HTMLElement) => {
    if (anchor && anchor.element === target) {
      cancelHide()
      return
    }
    const name = target.dataset.mention || (target.textContent || "").replace(/^@/, "")
    const user = props.users.find((u) => u.name === name)
    if (!user) {
      return
    }
    cancelHide()
    setPosition(undefined)
    setAnchor({ user, element: target, rect: target.getBoundingClientRect() })
  }

  const handleOver = (e: React.MouseEvent) => {
    const target = mentionTarget(e)
    if (target) {
      open(target)
    }
  }

  const handleOut = (e: React.MouseEvent) => {
    if (mentionTarget(e)) {
      scheduleHide()
    }
  }

  // Thiết bị cảm ứng không có hover: chạm để mở, chạm lần nữa để đóng
  const handleClick = (e: React.MouseEvent) => {
    const target = mentionTarget(e)
    if (!target) {
      return
    }
    e.preventDefault()
    if (anchor && anchor.element === target) {
      close()
    } else {
      open(target)
    }
  }

  const meta = anchor ? [anchor.user.organization, anchor.user.province].filter((part) => !!part).join(" · ") : ""

  return (
    <>
      <div className="c-mention-hover" onMouseOver={handleOver} onMouseOut={handleOut} onClick={handleClick}>
        {props.children}
      </div>
      {anchor &&
        ReactDOM.createPortal(
          <div
            ref={cardRef}
            className="c-mention-card"
            role="tooltip"
            style={{ top: position?.top ?? 0, left: position?.left ?? 0, visibility: position ? "visible" : "hidden" }}
            onMouseEnter={cancelHide}
            onMouseLeave={scheduleHide}
          >
            <Avatar user={{ name: anchor.user.name, avatarURL: anchor.user.avatarURL }} size="large" />
            <div className="c-mention-card__text">
              <span className="c-mention-card__name">{anchor.user.name}</span>
              {meta && <span className="c-mention-card__meta">{meta}</span>}
            </div>
          </div>,
          document.body
        )}
    </>
  )
}
